import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import {
  Megaphone,
  Plus,
  Search,
  Filter,
  Pin,
  Calendar,
  User,
  CheckCircle2,
  Trash2,
  Edit3,
  X,
  AlertTriangle,
  Info,
  Clock,
  Sparkles,
  Layers,
  ChevronRight,
  RefreshCw,
  Eye
} from 'lucide-react';
import { announcementsAPI } from '../services/api';

const SOCKET_SERVER_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/+$/, '');

const CATEGORIES = [
  'General',
  'Training & Workshop',
  'Advisory & Guidelines',
  'Weather Advisory',
  'Market & Pricing Bulletin',
  'Assistance & Subsidy',
  'Event & Meeting'
];

export function AnnouncementsPage({ user }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  
  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Form State for Admin Create / Edit
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetRole: 'all',
    priority: 'normal',
    category: 'General',
    attachmentUrl: '',
    isPinned: false
  });

  const isAdmin = user?.role === 'admin';
  const userId = user?.id || user?.username || 'user';
  const userRole = user?.role || 'farmer';

  // ── Fetch announcements ──────────────────────────────────────────
  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const res = await announcementsAPI.getAdminAll();
        if (res.success) {
          setAnnouncements(res.announcements || []);
        }
      } else {
        const res = await announcementsAPI.getFeed(userId, userRole);
        if (res.success) {
          setAnnouncements(res.announcements || []);
        }
      }
    } catch (err) {
      console.warn('[AnnouncementsPage] fetchFeed error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, userId, userRole]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // ── Socket.IO Real-time listeners ────────────────────────────────
  useEffect(() => {
    const socket = io(SOCKET_SERVER_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: true
    });

    socket.on('connect', () => {
      socket.emit('join', userId);
    });

    socket.on('new_announcement', () => {
      fetchFeed();
    });

    socket.on('announcement_updated', () => {
      fetchFeed();
    });

    socket.on('announcement_deleted', () => {
      fetchFeed();
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, fetchFeed]);

  // ── Handle Mark as Read ──────────────────────────────────────────
  const handleOpenDetail = async (item) => {
    setSelectedAnnouncement(item);
    if (!item.isRead && !isAdmin) {
      // Mark as read in state immediately for fast feedback
      setAnnouncements(prev =>
        prev.map(a => (a.id === item.id ? { ...a, isRead: true } : a))
      );
      await announcementsAPI.markRead(item.id, userId);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setAnnouncements(prev => prev.map(a => ({ ...a, isRead: true })));
      await announcementsAPI.markAllRead(userId, userRole);
    } catch (err) {
      console.warn('Mark all read error:', err);
    }
  };

  // ── Admin Create / Update ────────────────────────────────────────
  const handleOpenCreateModal = (item = null) => {
    setFormError('');
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        content: item.content,
        targetRole: item.targetRole || 'all',
        priority: item.priority || 'normal',
        category: item.category || 'General',
        attachmentUrl: item.attachmentUrl || '',
        isPinned: Boolean(item.isPinned)
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        content: '',
        targetRole: 'all',
        priority: 'normal',
        category: 'General',
        attachmentUrl: '',
        isPinned: false
      });
    }
    setShowCreateModal(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      setFormError('Please fill in both the title and content.');
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      if (editingItem) {
        await announcementsAPI.update(editingItem.id, formData);
      } else {
        const adminDisplayName = user?.fullname || (user?.firstName ? `${user?.firstName} ${user?.lastName || ''}`.trim() : '') || user?.username || 'Administrator';
        const payload = {
          ...formData,
          adminId: String(userId || 'admin_1'),
          adminName: adminDisplayName
        };
        await announcementsAPI.create(payload);
      }
      setShowCreateModal(false);
      fetchFeed();
    } catch (err) {
      setFormError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save announcement.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement? This action cannot be undone.')) {
      return;
    }
    try {
      await announcementsAPI.delete(id);
      setAnnouncements(prev => prev.filter(a => a.id !== id));
      if (selectedAnnouncement?.id === id) {
        setSelectedAnnouncement(null);
      }
    } catch (err) {
      alert('Failed to delete announcement: ' + err.message);
    }
  };

  // ── Filter announcements ─────────────────────────────────────────
  const filteredAnnouncements = useMemo(() => {
    return announcements.filter(item => {
      // Search
      const matchSearch =
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.adminName?.toLowerCase().includes(searchQuery.toLowerCase());

      // Target Role Filter
      const matchRole =
        selectedRoleFilter === 'all' ||
        item.targetRole === selectedRoleFilter ||
        item.targetRole === 'all';

      // Category Filter
      const matchCategory =
        selectedCategory === 'all' || item.category === selectedCategory;

      // Priority Filter
      const matchPriority =
        selectedPriority === 'all' || item.priority === selectedPriority;

      return matchSearch && matchRole && matchCategory && matchPriority;
    });
  }, [announcements, searchQuery, selectedRoleFilter, selectedCategory, selectedPriority]);

  const unreadCount = useMemo(() => {
    return announcements.filter(a => !a.isRead).length;
  }, [announcements]);

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-800 border border-rose-300 animate-pulse">
            <AlertTriangle className="w-3 h-3 text-rose-600" /> Urgent
          </span>
        );
      case 'important':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300">
            <Sparkles className="w-3 h-3 text-amber-600" /> Important
          </span>
        );
      case 'low':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-300">
            Low
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
            Normal
          </span>
        );
    }
  };

  const getTargetBadge = (targetRole) => {
    switch (targetRole) {
      case 'farmer':
        return <span className="bg-green-50 text-green-700 border border-green-200 text-[11px] font-semibold px-2 py-0.5 rounded-md">🌾 Farmers Only</span>;
      case 'processor':
        return <span className="bg-amber-50 text-amber-800 border border-amber-200 text-[11px] font-semibold px-2 py-0.5 rounded-md">🏭 Processors Only</span>;
      default:
        return <span className="bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-semibold px-2 py-0.5 rounded-md">🌐 All Accounts</span>;
    }
  };

  return (
    <div className="flex-1 min-h-screen bg-gradient-to-br from-amber-50/50 via-white to-emerald-50/30 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── Page Header ────────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-amber-900 via-amber-800 to-amber-950 rounded-2xl p-6 sm:p-8 text-white shadow-xl relative overflow-hidden">
          <div className="absolute right-0 top-0 translate-x-10 -translate-y-10 w-64 h-64 bg-amber-600/20 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-amber-300 text-xs font-bold uppercase tracking-wider mb-2">
                <Megaphone className="w-4 h-4" /> Official Broadcast Network
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white flex items-center gap-3">
                Announcements & Advisories
                {!isAdmin && unreadCount > 0 && (
                  <span className="bg-rose-500 text-white text-xs px-2.5 py-0.5 rounded-full font-bold shadow-sm animate-bounce">
                    {unreadCount} Unread
                  </span>
                )}
              </h1>
              <p className="text-amber-100/90 text-sm mt-1 max-w-2xl">
                Official program notices, training schedules, weather advisories, and cacao pricing updates from the BCIIS Administration.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={fetchFeed}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold backdrop-blur-sm transition border border-white/20"
                title="Refresh feed"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </button>

              {!isAdmin && unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-md hover:shadow-lg transition"
                >
                  <CheckCircle2 className="w-4 h-4" /> Mark All Read
                </button>
              )}

              {isAdmin && (
                <button
                  onClick={() => handleOpenCreateModal()}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 font-bold text-sm shadow-lg hover:shadow-amber-500/30 transition transform hover:-translate-y-0.5"
                >
                  <Plus className="w-4 h-4" /> Post New Announcement
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Search & Filter Controls ───────────────────────────── */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-amber-100 flex flex-col md:flex-row gap-3 items-center justify-between">
          {/* Search bar */}
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search announcements by title, keyword, or author..."
              className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500 transition"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Target Role Filter */}
            {isAdmin && (
              <select
                value={selectedRoleFilter}
                onChange={(e) => setSelectedRoleFilter(e.target.value)}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="all">All Audiences</option>
                <option value="farmer">🌾 Farmers Only</option>
                <option value="processor">🏭 Processors Only</option>
              </select>
            )}

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Priority Filter */}
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">All Priorities</option>
              <option value="urgent">🔴 Urgent</option>
              <option value="important">🟡 Important</option>
              <option value="normal">🟢 Normal</option>
            </select>
          </div>
        </div>

        {/* ── Announcements Feed ──────────────────────────────────── */}
        {loading ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-amber-100 shadow-sm">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-700 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-600">Loading announcements...</p>
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-dashed border-gray-300 shadow-sm">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-gray-800">No Announcements Found</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
              {searchQuery || selectedCategory !== 'all' || selectedPriority !== 'all'
                ? 'Try adjusting your search or filters to find what you are looking for.'
                : 'There are currently no active announcements for your account.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAnnouncements.map((item) => (
              <div
                key={item.id}
                onClick={() => handleOpenDetail(item)}
                className={`group relative bg-white rounded-2xl p-5 border transition-all duration-200 cursor-pointer flex flex-col justify-between hover:shadow-xl hover:-translate-y-1 ${
                  item.isPinned
                    ? 'border-amber-400 ring-2 ring-amber-300/30 bg-gradient-to-b from-amber-50/40 to-white'
                    : !item.isRead && !isAdmin
                    ? 'border-amber-300 bg-amber-50/20 shadow-sm'
                    : 'border-gray-200/80 hover:border-amber-300'
                }`}
              >
                <div>
                  {/* Top Badges & Pin */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {item.isPinned && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-amber-500 text-white shadow-xs">
                          <Pin className="w-3 h-3 fill-current" /> Pinned
                        </span>
                      )}
                      {getPriorityBadge(item.priority)}
                      {getTargetBadge(item.targetRole)}
                    </div>

                    {!item.isRead && !isAdmin && (
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-600 ring-4 ring-amber-100 flex-shrink-0 animate-pulse" title="Unread" />
                    )}
                  </div>

                  {/* Category Pill */}
                  <div className="text-[11px] font-semibold text-amber-800/80 uppercase tracking-wide mb-1">
                    {item.category || 'General'}
                  </div>

                  {/* Title */}
                  <h3 className="text-base font-bold text-gray-900 group-hover:text-amber-900 transition line-clamp-2 leading-snug">
                    {item.title}
                  </h3>

                  {/* Content snippet */}
                  <p className="text-xs text-gray-600 mt-2 line-clamp-3 leading-relaxed">
                    {item.content}
                  </p>
                </div>

                {/* Bottom Meta & Admin Controls */}
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500">
                  <div className="flex items-center gap-2 truncate">
                    <span className="flex items-center gap-1 truncate font-medium text-gray-700">
                      <User className="w-3 h-3 text-amber-700" /> {item.adminName || 'Admin'}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-gray-400" />
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent'}
                    </span>
                  </div>

                  {/* Admin actions or Read More link */}
                  {isAdmin ? (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <span className="text-[10px] text-gray-400 mr-1 flex items-center gap-0.5">
                        <Eye className="w-3 h-3" /> {item.totalReads || 0}
                      </span>
                      <button
                        onClick={() => handleOpenCreateModal(item)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-amber-700 hover:bg-amber-50 transition"
                        title="Edit announcement"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition"
                        title="Delete announcement"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-amber-700 font-bold flex items-center gap-0.5 group-hover:underline">
                      Read <ChevronRight className="w-3 h-3" />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── View Detail Modal ────────────────────────────────────── */}
        {selectedAnnouncement && (
          <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl overflow-hidden border border-amber-100 animate-scale-up">
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-800 via-amber-900 to-amber-950 p-6 text-white relative">
                <button
                  onClick={() => setSelectedAnnouncement(null)}
                  className="absolute right-4 top-4 text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {selectedAnnouncement.isPinned && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-bold bg-amber-500 text-white">
                      <Pin className="w-3 h-3 fill-current" /> Pinned
                    </span>
                  )}
                  {getPriorityBadge(selectedAnnouncement.priority)}
                  {getTargetBadge(selectedAnnouncement.targetRole)}
                  <span className="bg-white/10 text-amber-200 text-xs px-2.5 py-0.5 rounded-md font-medium border border-white/10">
                    {selectedAnnouncement.category || 'General'}
                  </span>
                </div>

                <h2 className="text-xl sm:text-2xl font-extrabold leading-tight text-white mt-1">
                  {selectedAnnouncement.title}
                </h2>

                <div className="flex items-center gap-3 text-xs text-amber-200/90 mt-3">
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" /> Posted by {selectedAnnouncement.adminName || 'Administrator'}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {selectedAnnouncement.createdAt ? new Date(selectedAnnouncement.createdAt).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' }) : 'Recent'}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-6 sm:p-8 max-h-[60vh] overflow-y-auto space-y-4">
                <div className="text-gray-800 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                  {selectedAnnouncement.content}
                </div>

                {selectedAnnouncement.attachmentUrl && (
                  <div className="mt-4 p-4 rounded-xl bg-amber-50/70 border border-amber-200">
                    <h4 className="text-xs font-bold text-amber-900 mb-1">Attached Document / Link</h4>
                    <a
                      href={selectedAnnouncement.attachmentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline break-all"
                    >
                      {selectedAnnouncement.attachmentUrl}
                    </a>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setSelectedAnnouncement(null)}
                  className="px-5 py-2 bg-amber-800 hover:bg-amber-900 text-white text-xs font-bold rounded-xl transition shadow-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Admin Post / Edit Modal ─────────────────────────────── */}
        {showCreateModal && (
          <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl overflow-hidden border border-amber-100 animate-scale-up">
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-800 to-amber-900 p-5 text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-amber-300" />
                  <h2 className="text-lg font-bold">
                    {editingItem ? 'Edit Announcement' : 'Post New Announcement'}
                  </h2>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmitForm} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
                {formError && (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0" /> {formError}
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Announcement Headline / Title <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Cacao GAP Training Workshop Schedule Announced"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                  />
                </div>

                {/* Target Audience & Priority Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Target Audience */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                      Target Audience <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={formData.targetRole}
                      onChange={(e) => setFormData({ ...formData, targetRole: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-xs font-medium focus:ring-2 focus:ring-amber-500 outline-none bg-white"
                    >
                      <option value="all">🌐 All Accounts (Farmers & Processors)</option>
                      <option value="farmer">🌾 Farmers Only</option>
                      <option value="processor">🏭 Processors Only</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                      Priority Level
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-xs font-medium focus:ring-2 focus:ring-amber-500 outline-none bg-white"
                    >
                      <option value="normal">🟢 Normal</option>
                      <option value="important">🟡 Important</option>
                      <option value="urgent">🔴 Urgent</option>
                      <option value="low">⚪ Low</option>
                    </select>
                  </div>
                </div>

                {/* Category & Pin Option */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-gray-300 text-xs font-medium focus:ring-2 focus:ring-amber-500 outline-none bg-white"
                    >
                      {CATEGORIES.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  <div className="pt-5">
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-700">
                      <input
                        type="checkbox"
                        checked={formData.isPinned}
                        onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                        className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500 border-gray-300"
                      />
                      <span>📌 Pin to top of feed</span>
                    </label>
                  </div>
                </div>

                {/* Content Message */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Announcement Body / Content <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    rows={6}
                    required
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Type the full announcement message, instructions, or advisory details here..."
                    className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none leading-relaxed"
                  />
                </div>

                {/* Optional Attachment Link */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                    Attachment URL or Document Link (Optional)
                  </label>
                  <input
                    type="url"
                    value={formData.attachmentUrl}
                    onChange={(e) => setFormData({ ...formData, attachmentUrl: e.target.value })}
                    placeholder="https://example.com/advisory.pdf"
                    className="w-full px-3.5 py-2 rounded-xl border border-gray-300 text-xs focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>

                {/* Submit button */}
                <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold text-xs shadow-md hover:shadow-amber-600/30 transition disabled:opacity-60 flex items-center gap-2"
                  >
                    {submitting ? 'Saving...' : editingItem ? 'Save Changes' : '📢 Publish & Broadcast'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default AnnouncementsPage;
